import { useJSONFetcher, JSONFetcherOptions } from "@outplayed/json-fetcher";
import { SPECIAL_TIER_LIST_PAGES } from "@ugg/shared/pages/tier-list-pages";
import { useValidateQueryParams } from "@ugg/shared/query-params/params-helpers";
import { validParams } from "@ugg/shared/query-params/valid-params";

// ─────────────────────────────────────────────────────────────────────
// ARAM Mayhem shared data layer
// ─────────────────────────────────────────────────────────────────────
//
// Types, cleaners, and JSONFetcher hooks for the ARAM Mayhem custom
// data pipeline. Lives in the shared package so both the FE-specific
// tier-list surfaces (AramMayhemTierListContainer + friends in the
// main-app package) and shared consumers (e.g. AramMayhemSEO on the
// champion profile page) can use the same fetch layer — same URLs,
// same cache slots, so useJSONFetcher dedupes across renderers on
// the same page with no additional network cost.
//
// This module is deliberately data-only: no UI, no styling
// constants, no React components. FE surfaces that need
// presentation-layer constants (tier color maps, badge classes,
// etc.) define those next to their components; only the raw shape
// definitions (tier-name literal type, rarity literal type,
// interfaces) live here.

// TIER_ORDER is the single source of truth for valid tier names —
// both the type below and any render loop that iterates tiers
// derive from it. `as const` widens it to a literal-tuple type so
// TierNameValue resolves to "S+" | "S" | "A" | "B" | "C" | "D"
// rather than string.
export const TIER_ORDER = ["S+", "S", "A", "B", "C", "D"] as const;
export type TierNameValue = "S+" | "S" | "A" | "B" | "C" | "D";

// Narrow set of rarities that participate in the by-rarity augment
// tier list. Deliberately narrower than the manifest's `rarity`
// field (which is string-typed to preserve values like kEventChoice
// and kBronze that the tier lists don't render) — this type
// constrains only the tier-list JSON shape, not the manifest.
export type AugmentRarity = "kPrismatic" | "kGold" | "kSilver";

// ─────────────────────────────────────────────────────────────────────
// JSON payload interfaces
// ─────────────────────────────────────────────────────────────────────

export interface AugmentTierList {
  rarities: Record<AugmentRarity, { tiers: Record<TierNameValue, number[]> }>;
  lastUpdated: string;
}

export interface AugmentAggregatedTierList {
  tiers: Record<TierNameValue, number[]>; // integer augment IDs
  lastUpdated: string;
}

// Manifest shape — keyed by stringified augment id (JSON object keys
// have to be strings; the integer id is also carried inside each
// entry for code that needs to iterate by number). The manifest is
// the canonical source of truth for augment metadata across the page:
// the tier lists carry just ids, and consumers cross-reference here
// to get icons + display names. `rarity` is left as `string` rather
// than the narrow AugmentRarity union because the upstream
// cherry-augments.json carries values outside the three tier-list
// rarities (kEventChoice, kBronze) — the manifest preserves them
// even though the tier-list pages only show three.
export interface AugmentManifestEntry {
  id: number;
  augmentNameId: string;
  nameTRA: string;
  rarity: string;
  iconUrl: string;
}

export type AugmentManifest = Record<string, AugmentManifestEntry>;

// Type matches the JSON shape — object with two named keys, not a
// tuple. Records constrain to the 6 valid tier letters so
// `data.tiers.foo` would be a compile-time error.
//
// Tier values are numeric champion KEYS (62, 145, ...) — NOT the
// internal SKIN strings ("MonkeyKing", "Kaisa"). data_tierlists-html.py
// resolves SKIN → key via champion_id_to_key-{patch}.json before
// serializing, so the JSON we receive is already in the same
// key format the site uses everywhere else.
export interface AramMayhemChampionTierList {
  tiers: Record<TierNameValue, number[]>;
  lastUpdated: string;
}

// ─────────────────────────────────────────────────────────────────────
// Cleaners — normalize the raw fetch payload into consumer-friendly
// shape (rename `tiers` → `tierlist`, seed empty defaults for the
// loading state so consumers can dereference without null-checks).
// ─────────────────────────────────────────────────────────────────────

// cleanAramMayhemChampionTierList pulls fields off `data` instead of returning stubs.
// Optional-chaining handles the `data === null | undefined` case
// the fetcher might pass during loading; nullish-coalescing seeds
// defaults.
export function cleanAramMayhemChampionTierList(data: AramMayhemChampionTierList | null | undefined) {
  const tierlist = data?.tiers ?? ({} as Record<TierNameValue, number[]>);
  const lastUpdated = data?.lastUpdated ?? "";
  return { tierlist, lastUpdated };
}

// cleanAramMayhemAugmentTierList — same shape as the champion clean
// helper (defaults to empty record + empty string during loading) but
// the tier values are number arrays, not string arrays. Lives next to
// its hook for the same reason the champion pair does: they're a
// fetch-and-shape unit.
export function cleanAramMayhemAugmentTierList(data: AugmentAggregatedTierList | null | undefined) {
  const tierlist = data?.tiers ?? ({} as Record<TierNameValue, number[]>);
  const lastUpdated = data?.lastUpdated ?? "";
  return { tierlist, lastUpdated };
}

// cleanAramMayhemAugmentByRarityTierList — peels off the top-level
// `rarities` object instead of `tiers` (the by-rarity JSON nests the
// tier dict one level deeper, under each rarity). Same loading-state
// fallback pattern as the other clean helpers.
export function cleanAramMayhemAugmentByRarityTierList(data: AugmentTierList | null | undefined) {
  const rarities = data?.rarities ?? ({} as Record<AugmentRarity, { tiers: Record<TierNameValue, number[]> }>);
  const lastUpdated = data?.lastUpdated ?? "";
  return { rarities, lastUpdated };
}

// cleanAramMayhemAugmentManifest — simpler than the tier-list cleaners
// because the manifest is just a flat id→entry dict (no `tiers` /
// `rarities` wrapper, no `lastUpdated`). The empty-object fallback
// keeps the consumer's `manifest[id]` lookup safe during the loading
// state without forcing every callsite to null-check the dict itself.
export function cleanAramMayhemAugmentManifest(data: AugmentManifest | null | undefined) {
  return data ?? ({} as AugmentManifest);
}

// ─────────────────────────────────────────────────────────────────────
// Fetch hooks — thin JSONFetcher wrappers around the CDN URLs. All
// hit static.bigbrain.gg/custom-aram-mayhem/<patch>/... produced by
// the ugg-replay-stats-aggregator pipeline.
// ─────────────────────────────────────────────────────────────────────

export function useAramMayhemChampionTierList<K = ReturnType<typeof cleanAramMayhemChampionTierList>>(
  options?: { params?: Record<string, any> } & JSONFetcherOptions<AramMayhemChampionTierList, K>,
) {
  const { params = {}, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_TIER_LIST, params, true);

  const { patch } = validatedQueryParams;
  const url = `https://static.bigbrain.gg/custom-aram-mayhem/${patch}/tierlist-champions-${patch}.json`;

  if (onCompleted) {
    return useJSONFetcher<AramMayhemChampionTierList, K>(url, {
      skip,
      ssr,
      onCompleted,
    });
  }

  return useJSONFetcher<AramMayhemChampionTierList, ReturnType<typeof cleanAramMayhemChampionTierList>>(url, {
    skip,
    ssr,
    onCompleted: (url, json) => cleanAramMayhemChampionTierList(json),
  });
}

// Fetches the aggregated (global, non-rarity-grouped) augment tier
// list — all augments compete in one EMR distribution regardless of
// rarity. Companion to the by-rarity hook below; either can be used
// independently. URL pattern mirrors the champion endpoint, swapping
// `tierlist-champions` for `tierlist-augments`.
export function useAramMayhemAugmentTierList<K = ReturnType<typeof cleanAramMayhemAugmentTierList>>(
  options?: { params?: Record<string, any> } & JSONFetcherOptions<AugmentAggregatedTierList, K>,
) {
  const { params = {}, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_TIER_LIST, params, true);

  const { patch } = validatedQueryParams;
  const url = `https://static.bigbrain.gg/custom-aram-mayhem/${patch}/tierlist-augments-${patch}.json`;

  if (onCompleted) {
    return useJSONFetcher<AugmentAggregatedTierList, K>(url, {
      skip,
      ssr,
      onCompleted,
    });
  }

  return useJSONFetcher<AugmentAggregatedTierList, ReturnType<typeof cleanAramMayhemAugmentTierList>>(url, {
    skip,
    ssr,
    onCompleted: (url, json) => cleanAramMayhemAugmentTierList(json),
  });
}

// Fetches the by-rarity augment tier list — same data source as the
// aggregated hook above, but with three independent EMR distributions
// (one per rarity) instead of one global pool. The component renders
// these as three stacked sections.
export function useAramMayhemAugmentByRarityTierList<K = ReturnType<typeof cleanAramMayhemAugmentByRarityTierList>>(
  options?: { params?: Record<string, any> } & JSONFetcherOptions<AugmentTierList, K>,
) {
  const { params = {}, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};

  const validateQueryParams = useValidateQueryParams(validParams);
  const validatedQueryParams = validateQueryParams(SPECIAL_TIER_LIST_PAGES.ARAM_MAYHEM_TIER_LIST, params, true);

  const { patch } = validatedQueryParams;
  const url = `https://static.bigbrain.gg/custom-aram-mayhem/${patch}/tierlist-augments-by-rarity-${patch}.json`;

  if (onCompleted) {
    return useJSONFetcher<AugmentTierList, K>(url, {
      skip,
      ssr,
      onCompleted,
    });
  }

  return useJSONFetcher<AugmentTierList, ReturnType<typeof cleanAramMayhemAugmentByRarityTierList>>(url, {
    skip,
    ssr,
    onCompleted: (url, json) => cleanAramMayhemAugmentByRarityTierList(json),
  });
}

// Fetches the augment manifest — one per patch. Used by the augment
// tier-list tables to render real icons + names instead of placeholder
// content, and by the champion-page SEO blurb to resolve S+ augment
// ids to display names. URL pattern mirrors the tier-list endpoints
// (same patch substitution on both sides), pointing at
// `/custom-aram-mayhem/<patch>/aram-mayhem-augment-manifest-<patch>.json`
// where the manifest is uploaded by the AWS step.
//
// Params handling mirrors useAramMayhemPerChampionAugmentTierList
// below — patch is read directly from caller-supplied `params` rather
// than routed through validateQueryParams, so the hook works from any
// route (the build page, the tier-list pages, the SEO block, anywhere
// a manifest reference appears) without being tied to a specific
// tier-list page identifier. Callers are responsible for ensuring
// `patch` is in the params dict themselves; effectiveSkip below
// short-circuits the fetch when it's missing so a malformed URL like
// `.../undefined/aram-mayhem-augment-manifest-undefined.json` never
// hits the CDN.
export function useAramMayhemAugmentManifest<K = ReturnType<typeof cleanAramMayhemAugmentManifest>>(
  options?: { params?: Record<string, any> } & JSONFetcherOptions<AugmentManifest, K>,
) {
  const { params = {}, ssr = false, skip = false, onCompleted, customCacheKey } = options || {};

  const { patch } = params;
  const effectiveSkip = skip || !patch;

  const url = `https://static.bigbrain.gg/custom-aram-mayhem/${patch}/aram-mayhem-augment-manifest-${patch}.json`;

  if (onCompleted) {
    return useJSONFetcher<AugmentManifest, K>(url, {
      skip: effectiveSkip,
      ssr,
      onCompleted,
    });
  }

  return useJSONFetcher<AugmentManifest, ReturnType<typeof cleanAramMayhemAugmentManifest>>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted: (url, json) => cleanAramMayhemAugmentManifest(json),
  });
}

// Fetches the per-champion augment tier list — one champion's worth
// of augment data, in the same shape as the aggregated augment tier
// list (`{ tiers: Record<TierNameValue, number[]>, lastUpdated }`).
// The underlying JSON is one file per champion under
// `tierlist-per-champion-augments-<patch>/` on the CDN, written by
// data_tierlists-html.py.
//
// Differs from the other hooks in this file in two ways:
//
// 1. The hook is consumed on the champion-build page (a different
//    route from the tier-list pages), where `useValidateQueryParams`
//    can't be relied on to surface the patch — that helper validates
//    against a specific tier-list page identifier, and we're not on
//    one. Instead we read `patch` directly off the caller-supplied
//    `params` dict; the caller is responsible for plumbing it in
//    (in practice, AramMayhemBuild runs validateQueryParams against
//    useChampionProfileContext().page and forwards the result).
// 2. The fetch is keyed by championKey + patch BOTH, so the hook
//    takes `championKey` as an explicit named option rather than
//    relying on params alone. championKey is the numeric champion
//    identifier (62 for MonkeyKing, 145 for Kaisa, ...) — matches
//    the filename stem the Python side writes AFTER resolving the
//    SKIN → key mapping. Callsites pass the raw championId they
//    already hold, no name-to-key lookup needed.
//
// The cleaning helper is reused from `cleanAramMayhemAugmentTierList`
// above — the JSON shape is identical, so it'd be wasteful to define
// a second copy. The hook name is per-champion-specific so callsites
// stay clear about which endpoint they're hitting.
export function useAramMayhemPerChampionAugmentTierList<K = ReturnType<typeof cleanAramMayhemAugmentTierList>>(
  options: { championKey: number; params?: Record<string, any> } & JSONFetcherOptions<AugmentAggregatedTierList, K>,
) {
  const { championKey, params = {}, ssr = false, skip = false, onCompleted, customCacheKey } = options;

  // Extract patch directly without running through
  // validateQueryParams — see the comment above for why. If the
  // caller forgot to plumb patch through, the fetch is skipped
  // below rather than firing a request to a malformed URL.
  const { patch } = params;

  // Skip the fetch when either URL component is missing — keeps
  // the JSONFetcher cache key stable across renders (no flapping
  // between a "loading" state on the malformed URL and a real
  // request once the data is available), and avoids hitting the
  // CDN with garbage like ".../tierlist-augments-undefined-undefined.json"
  // during SSR / initial mount before context is hydrated.
  // championKey === 0 is treated as missing here too — Riot's key
  // numbering starts at 1, so 0 is a sentinel for "not resolved yet".
  const effectiveSkip = skip || !patch || !championKey;

  const url = `https://static.bigbrain.gg/custom-aram-mayhem/${patch}/tierlist-per-champion-augments-${patch}/tierlist-augments-${championKey}-${patch}.json`;

  if (onCompleted) {
    return useJSONFetcher<AugmentAggregatedTierList, K>(url, {
      skip: effectiveSkip,
      ssr,
      onCompleted,
    });
  }

  return useJSONFetcher<AugmentAggregatedTierList, ReturnType<typeof cleanAramMayhemAugmentTierList>>(url, {
    skip: effectiveSkip,
    ssr,
    onCompleted: (url, json) => cleanAramMayhemAugmentTierList(json),
  });
}
